import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import PromptsListComponent from './PromptsListComponent';
import PromptEditModal from './PromptEditModal';
import { SystemPrompt } from '../../services/promptService';

interface CustomTabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const CustomTabPanel = memo(function CustomTabPanel(props: CustomTabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className="text-sm"
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
});

const SystemPromptsContent = () => {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<SystemPrompt | undefined>();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const translationPath = 'system_management.system_prompts';
  const tabs = [
    {
      id: 'chat',
      label: t(`${translationPath}.chat`),
    },
    {
      id: 'social_media',
      label: t(`${translationPath}.social_media`),
    },
    {
      id: 'ad_generation',
      label: t(`${translationPath}.ad_generation`),
    },
    {
      id: 'newsletter',
      label: t(`${translationPath}.newsletter`),
    },
    {
      id: 'product_description',
      label: t(`${translationPath}.product_description`),
    },
    {
      id: 'category_description',
      label: t(`${translationPath}.category_description`),
    },
  ];

  const currentFeature = tabs[value].id;

  const handleEditPrompt = (prompt: SystemPrompt) => {
    setSelectedPrompt(prompt);
    setEditModalOpen(true);
  };

  const handleCreatePrompt = () => {
    setSelectedPrompt(undefined);
    setEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setSelectedPrompt(undefined);
  };

  const handleSavePrompt = () => {
    // Trigger refresh of the prompts list
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <div className="space-y-6">
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="basic tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab, index) => (
          <Tab
            className="text-sm"
            key={tab.id}
            label={tab.label}
            sx={{ textTransform: 'none' }}
            {...a11yProps(index)}
          />
        ))}
      </Tabs>

      {tabs.map((tab, index) => (
        <CustomTabPanel key={tab.id} value={value} index={index}>
          <PromptsListComponent
            feature={tab.id}
            onEditPrompt={handleEditPrompt}
            onCreatePrompt={handleCreatePrompt}
            refreshTrigger={refreshTrigger}
          />
        </CustomTabPanel>
      ))}

      <PromptEditModal
        open={editModalOpen}
        onClose={handleCloseModal}
        onSave={handleSavePrompt}
        prompt={selectedPrompt}
        feature={currentFeature}
      />
    </div>
  );
};

export const SystemPrompts = () => {
  return <SystemPromptsContent />;
};
